<script setup lang="ts">
import CurrencyText from '@uiKit/components/CurrencyText.vue'
import { computed, ref, watch } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Message from '@uiKit/elements/Message.vue'
import Text from '@uiKit/elements/Text.vue'
import FormField from '@uiKit/components/FormField.vue'
import FileUpload from '@uiKit/components/FileUpload.vue'

interface Props {
  /** Pre-formatted deposit amount, e.g. `"5,000"`. */
  amount: string
  currency?: string
  submitError?: string | null
  submitting?: boolean
}

withDefaults(defineProps<Props>(), {
  currency: 'AED',
  submitError: null,
  submitting: false,
})

const emit = defineEmits<{
  (e: 'validity-change', value: boolean): void
}>()

const file = ref<File | null>(null)
const attachmentError = ref('')

watch(file, () => {
  if (attachmentError.value) attachmentError.value = ''
})

const isValid = computed(() => Boolean(file.value))

watch(isValid, (value) => emit('validity-change', value), { immediate: true })

/**
 * Called by the parent when the footer button is pressed. Returns the payload
 * when everything checks out, or `null` after surfacing inline errors — the
 * footer lives in the dialog chrome, so validation has to be reachable from it.
 */
function collect(): { attachment: File } | null {
  const attachment = file.value
  attachmentError.value = attachment ? '' : 'Upload a screenshot or receipt of your transfer.'
  if (!attachment) return null
  return { attachment }
}

defineExpose({ collect })
</script>

<template>
  <Box direction="col" gap="6" class="dcs">
    <Box direction="col" gap="3">
      <Text type="body" weight="bold" color="muted" is="h3">Deposit Details</Text>

      <Box direction="col" class="dcs__summary">
        <Box align="center" justify="between" gap="4" class="dcs__summary-row">
          <Text type="heading" weight="regular" color="muted" is="span">Amount</Text>
          <Text type="heading" weight="medium" align="right" is="span" class="dcs__amount">
            <CurrencyText :text="`${currency} ${amount}`" />
          </Text>
        </Box>
        <Box align="center" justify="between" gap="4" class="dcs__summary-row">
          <Text type="heading" weight="regular" color="muted" is="span">Status</Text>
          <Text type="heading" weight="medium" align="right" color="var(--p-warning-500)" is="span">
            Initiated
          </Text>
        </Box>
      </Box>
    </Box>

    <FormField label="Upload Receipt" required :error="attachmentError">
      <FileUpload
        v-model="file"
        prompt="Drag and drop or click to upload receipt"
        :accept-types="['application/pdf', 'image/jpeg', 'image/jpg', 'image/png']"
        accept-label="PDF, JPEG, or PNG"
        :invalid="!!attachmentError"
      />
    </FormField>

    <Text type="body" color="muted" is="p">
      Upload a screenshot or receipt of your bank transfer.
    </Text>

    <Message v-if="submitError" color="danger" variant="outlined" :message="submitError" />
  </Box>
</template>

<style scoped>
.dcs__summary {
  background: var(--p-surface-0);
  border: 1px solid var(--p-content-border-color);
  border-radius: 12px;
  padding: 0 1.25rem;
}

.dcs__summary-row {
  padding: 0.8125rem 0;
}

.dcs__summary-row + .dcs__summary-row {
  border-top: 1px solid var(--p-content-border-color);
}

.dcs__amount {
  font-variant-numeric: tabular-nums;
}
</style>
